import { Fragment, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useQuery } from "@apollo/client";

import { FormGroup, Card, PageHeader, Button } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import Helmet from "components/helmet/Helmet";
import Documents, { DocumentOptionalColumns } from "components/documents/Documents";

import {
	Language,
	Fields,
	hasOptionalFieldPermission,
	usePermission,
	hasPluginPermission,
	DataFormat,
} from "imports/Utils";

import UserPushSettingsDialog from "./components/dialogs/UserPushSettingsDialog";

import { AccountType, DocumentEntity, DocumentType } from "__generated__/enums";
import { GetAccountByIdDocument } from "__generated__/view/users/graphql/user.queries.generated";

const UserDetails = () => {

	const session = useStoreState(store => store.session);
	const navigate = useNavigate();
	const { id } = useParams();
	const { isAdmin, isReadOnly } = usePermission();

	const [ dialogOpen, openDialog ] = useState(false);

	const { data, loading, refetch } = useQuery(GetAccountByIdDocument, {
		variables: {
			id: Number(id),
		},
	});

	const user = data?.getAccountById;

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("USER")}
					<Helmet title={intl.formatMessage({ id: "USER" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isReadOnly && (
						<Button onClick={() => openDialog(true)} loading={loading} variant="outlined">
							{Language("USER_JSON_PUSH_SETTINGS_EDIT")}
						</Button>
					)}

					<Button onClick={() => navigate("/view/users")} loading={loading} variant="outlined">
						{Language("NAV_USERS")}
					</Button>

					{!isReadOnly && (
						<Button onClick={() => navigate(`/view/users/${user?.id}/edit`)} loading={loading}>
							{Language("EDIT")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={6}>
					<Card>
						<FormGroup labelPlacement="left-center" label={Language("USER_USERNAME")} overflow="hidden">
							{user?.username || "-"}
						</FormGroup>

						<FormGroup labelPlacement="left-center" label={Language("USER_FULLNAME")} overflow="hidden">
							{user?.displayName || "-"}
						</FormGroup>

						<FormGroup labelPlacement="left-center" label={Language("USER_TYPE")} overflow="hidden">
							{DataFormat.userType(user?.accountType) || "-"}
						</FormGroup>

						{user?.accountType === AccountType.Operator && (
							<Fragment>
								<FormGroup labelPlacement="left-center" label={Language("SHIPS_SINGLE")} overflow="hidden">
									{user.ship?.name || "-"}
								</FormGroup>

								<FormGroup labelPlacement="left-center" label={Language("OPERATOR")} overflow="hidden">
									{user.relation?.name || "-"}
								</FormGroup>
							</Fragment>
						)}

						{user?.accountType === AccountType.Terminal && (
							<FormGroup labelPlacement="left-center" label={Language("LOCATION_MANAGER")} overflow="hidden">
								{user.relation?.name || "-"}
							</FormGroup>
						)}

						{user?.accountType === AccountType.Customer && (
							<FormGroup labelPlacement="left-center" label={Language("CLIENT_SINGLE")} overflow="hidden">
								{user.relation?.name || "-"}
							</FormGroup>
						)}

						<FormGroup labelPlacement="left-center" label={Language("USER_EMAIL")} overflow="hidden">
							{user?.email || "-"}
						</FormGroup>

						{hasOptionalFieldPermission(session, Fields.USER, "secondEmail") && (
							<Fragment>
								<FormGroup labelPlacement="left-center" label={Language("USER_SECONDARY_EMAIL")} overflow="hidden">
									{user?.secondEmail || "-"}
								</FormGroup>
							</Fragment>
						)}

						{(session.user?.properties.id === user?.id || isAdmin) &&
							<FormGroup labelPlacement="left-center" label={Language("SECONDARY_EMAIL_TYPE")} overflow="hidden">
								{user?.userEmailNotificationType ? Language(`EMAIL_NOTIFICATION_TYPE_${user.userEmailNotificationType }`) : "-"}
							</FormGroup>
						}

						<FormGroup labelPlacement="left-center" label={Language("USER_JSON_PUSH_SETTINGS")} overflow="hidden">
							{user?.jsonPushSettings?.jsonUrl || "-"}
						</FormGroup>

						{hasOptionalFieldPermission(session, Fields.USER, "phone") && (
							<FormGroup labelPlacement="left-center" label={Language("USER_PHONE")} overflow="hidden">
								{user?.phone || "-"}
							</FormGroup>
						)}

						{hasOptionalFieldPermission(session, Fields.USER, "mobilePhone") && (
							<FormGroup labelPlacement="left-center" label={Language("USER_MOBILE")} overflow="hidden">
								{user?.mobilePhone || "-"}
							</FormGroup>
						)}

						<FormGroup labelPlacement="left-center" label={Language("USER_LANGUAGE")} overflow="hidden">
							{user?.language?.description || "-"}
						</FormGroup>

						<FormGroup labelPlacement="left-center" label={Language("PREFERRED_CURRENCY")} overflow="hidden">
							{user?.preferredCurrency?.isoCode || "-"}
						</FormGroup>

						<FormGroup
							labelPlacement="left"
							labelPaddingTop={0}
							label={Language("USER_FLEETS")}
							overflow="visible">
							{user?.fleets?.map(fleet => fleet.description).join(", ") || "-"}
						</FormGroup>

						{hasOptionalFieldPermission(session, Fields.USER, "allowSystemInput") && (
							<FormGroup labelPlacement="left-center" label={Language("USER_ALLOW_SYSTEM_INPUT")} overflow="hidden">
								{user?.allowSystemInput ? Language("YES") : Language("NO")}
							</FormGroup>
						)}

						{hasOptionalFieldPermission(session, Fields.USER, "batchMessages") && (
							<FormGroup labelPlacement="left-center" label={Language("USER_NOTIFICATION_SETTINGS")} overflow="hidden">
								{user?.batchMessages || "-"}
							</FormGroup>
						)}
					</Card>

					{user?.accountType === AccountType.Operator && user.ship && hasPluginPermission(session, "skipperCertificates") && (
						<div style={{ marginTop: 16 }}>
							<Card title={Language("CERTIFICATES")}>
								<Documents
									entities={[ {
										id: user.ship.id,
										name: user.ship.name,
										type: DocumentEntity.Ship,
									} ]}
									showOptionalColumns={[
										DocumentOptionalColumns.DocumentType,
										DocumentOptionalColumns.IssuedAt,
										DocumentOptionalColumns.ValidUntil,
									]}
									documentTypeOptions={[
										DocumentType.Certificate,
										DocumentType.Map,
										DocumentType.TonnageCertificate,
										DocumentType.Insurance,
										DocumentType.Other,
									]} />
							</Card>
						</div>
					)}
				</Grid>
			</Grid>

			{user && (
				<UserPushSettingsDialog
					open={dialogOpen}
					onClose={() => openDialog(false)}
					user={user}
					refetch={() => refetch()} />
			)}
		</div>
	);
};

export default UserDetails;
